<template>
  <div class="consumer-dashboard">
    <!-- Header commun aux dashboards : sélecteur de cantine, date, service.
         La cantine sélectionnée pilote l'aiguillage vers NutriQuest (voir onContextChange).

         `consumer` n'est pas décoratif : sans lui, le header charge le contexte par les deux
         endpoints du personnel (`canteen/<id>/services/` et `canteens/<id>`), auxquels un convive
         n'a pas accès. Le 403 est rattrapé en silence, l'écran reste donc sans services **et**
         avec les flags à leur défaut — `featureDashboard` à `true` : l'aiguillage vers NutriQuest
         ne se déclenchait jamais. La prop route la lecture vers `consumerContext/`, l'endpoint
         minimisé ouvert au convive (arbitrage A de `adult-meal-feedback` §5.3.3). -->
    <dashboard-context-header title="MON ESPACE" consumer @context-change="onContextChange" />

    <div class="cards-grid">
      <!-- Mon assiette : retour repas du jour. Réutilise le panneau existant tel quel
           (capacité meal-feedback), la cantine et le service venant du contexte. -->
      <meal-feedback-panel
        v-if="featureMealFeedback"
        :canteen-id="canteenId"
        :service-id="selectedService"
        :date="selectedDate"
      />

      <!-- Mes avis : la page existante montée en modale, sans quitter le dashboard. -->
      <dashboard-modal-card
        title="💬 Mes avis"
        modal-title="Donner ou voir mes avis"
        description="Répondre aux questionnaires et retrouver mes réponses"
        link-label="Voir mes avis"
      >
        <template #default="{ isOpen }">
          <survey-links-panel v-if="isOpen" />
        </template>
      </dashboard-modal-card>

      <!-- Recherche de cantine : page publique, gardée en navigation (arbitrage §7.1). -->
      <dashboard-card
        :to="{ name: 'CanteenSearchLanding' }"
        title="🔍 Rechercher une cantine"
        description="Trouver une cantine et consulter sa fiche"
        link-label="Ouvrir la recherche"
      />

      <!-- Actions de ma cantine : liste ouverte au convive, filtrée côté API (§4).
           Gardée en navigation pour l'instant — écran de liste, arbitrage §7.1. -->
      <dashboard-card
        :to="{ name: 'ActionPlan' }"
        title="📋 Les actions de ma cantine"
        description="Les actions anti-gaspillage qui me concernent"
        link-label="Voir les actions"
      />

      <!-- Le module pédagogique, désormais **proposé** et non plus seulement atteignable :
           `/nutriquest` était ouvert à tout convive sans qu'aucun écran n'y mène. La carte suit
           `show_pedagogical`, flag distinct de `show_dashboard` (arbitrage §9.5) — le module se
           consomme **avec** un dashboard, et pas seulement à sa place. -->
      <dashboard-card
        v-if="featurePedagogical"
        :to="{ name: 'NutriQuest' }"
        title="🎯 NutriQuest"
        description="Mes parcours, mes défis et mes compétences"
        link-label="Continuer mon parcours"
      />

      <!-- Contenus d'explication : éditorial long, gardé en navigation (arbitrage §7.1). -->
      <dashboard-card
        :to="{ name: 'KezakoPage' }"
        title="📖 Kezako"
        description="Comprendre la démarche et les repères de la cantine"
        link-label="Découvrir"
      />
    </div>
  </div>
</template>

<script>
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"
import DashboardCard from "@/components/DashboardCard.vue"
import DashboardModalCard from "@/components/DashboardModalCard.vue"
import MealFeedbackPanel from "@/components/MealFeedbackPanel.vue"
import SurveyLinksPanel from "@/views/SurveyLinksPage/SurveyLinksPanel.vue"

/**
 * Dashboard du convive, au format card + fenêtre modale comme les dashboards de
 * personnel. L'écran dépend du flag `show_dashboard` de la cantine sélectionnée :
 * à `False`, le convive est renvoyé vers NutriQuest (mode MOOC). L'aiguillage suit
 * la cantine sélectionnée dans le header, un convive pouvant en avoir plusieurs.
 *
 * `show_pedagogical` est un flag **distinct** (arbitrage §9.5) : le module pédagogique se
 * consomme **avec** un dashboard, et pas seulement à sa place. Le mode MOOC en reste le cas
 * limite — sans dashboard, NutriQuest est tout l'écran.
 */
export default {
  name: "ConsumerDashboard",

  components: {
    DashboardContextHeader,
    DashboardCard,
    DashboardModalCard,
    MealFeedbackPanel,
    SurveyLinksPanel,
  },

  data() {
    return {
      canteenId: null,
      selectedDate: null,
      selectedService: null,
      featureMealFeedback: true,
      featurePedagogical: true,
    }
  },

  methods: {
    onContextChange(context) {
      this.canteenId = context.canteenId
      this.selectedDate = context.selectedDate
      this.selectedService = context.selectedService
      this.featureMealFeedback = context.featureMealFeedback ?? true
      this.featurePedagogical = context.featurePedagogical ?? true

      // Aiguillage par le flag de la cantine sélectionnée : à `False`, le convive
      // n'a pas de dashboard — il est renvoyé vers NutriQuest (mode MOOC). `replace`
      // et non `push` : la bascule ne doit pas laisser d'entrée dans l'historique.
      if (context.featureDashboard === false) {
        this.$router.replace({ name: "NutriQuest" })
      }
    },
  },
}
</script>

<style scoped>
.consumer-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}
</style>
